import React, { useEffect, useState, useCallback } from "react";
import { Link, useLocation, useHistory } from "react-router-dom";
import { debounce } from "lodash";
import Select from "react-select";

// Services & Types
import { MemberService } from "services/member.service";
import { DepartmentService } from "services/department.service";
import { EcodimeClassService } from "services/ecodime-class.service";
import { hasAnyAuthority, ROLES } from "services/security.utils";
import { IMemberResponseDTO, IUser, Gender, IDepartmentFlatDTO } from "models/church.types";
import UserService from "services/user.service";

export default function Members() {
  const location = useLocation();
  const history = useHistory();

  const [currentUser, setCurrentUser] = useState<IUser | null>(null);
  const [loadingUser, setLoadingUser] = useState(true);

  // Vérification des privilèges administrateur
  const isAdmin = hasAnyAuthority(currentUser?.authorities, [ROLES.ADMIN]);

  // Lecture des paramètres d'URL synchronisés
  const queryParams = new URLSearchParams(location.search);
  const deptIdFromUrl = queryParams.get("deptId");
  const ecodimeClassIdFromUrl = queryParams.get("ecodimeClassId");
  const isChildFromUrl = queryParams.get("isChild") === "true";

  const [members, setMembers] = useState<IMemberResponseDTO[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState("");

  // Catalogues d'options
  const [deptOptions, setDeptOptions] = useState<{ value: string; label: string }[]>([]);
  const [ecodimeOptions, setEcodimeOptions] = useState<{ value: string; label: string }[]>([]);

  // Pagination
  const [currentPage, setCurrentPage] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const itemsPerPage = 8;

  // 1. Chargement initial des référentiels
  useEffect(() => {
    UserService.getCurrentAccount()
      .then((res) => setCurrentUser(res.data))
      .catch(() => console.error("Erreur chargement profil"))
      .finally(() => setLoadingUser(false));

    const loadDataCatalogues = async () => {
      try {
        const [depts, ecodimeClasses] = await Promise.all([
          DepartmentService.getAllSimple(),
          EcodimeClassService.getAllSimple(),
        ]);

        setDeptOptions(depts.map((d: any) => ({ value: d.id.toString(), label: d.name })));
        setEcodimeOptions(ecodimeClasses.map((c: any) => ({ value: c.id.toString(), label: c.name })));
      } catch (err) {
        console.error("Erreur chargement des référentiels de filtres:", err);
      }
    };
    loadDataCatalogues();
  }, []);

  // 2. Fonction de chargement unifiée multicritères
  const loadData = useCallback(
    async (
      query: string,
      page: number,
      deptId: string | null,
      ecodimeClassId: string | null,
      isChild: boolean
    ) => {
      setLoading(true);
      try {
        const result = await MemberService.search(query, page, itemsPerPage, {
          departmentId: deptId || undefined,
          ecodimeClassId: ecodimeClassId || undefined,
          isChild: isChild ? true : undefined,
        });
        setMembers(result.data);
        setTotalItems(result.totalCount);
      } catch (error) {
        console.error("Erreur chargement membres:", error);
      } finally {
        setLoading(false);
      }
    },
    [itemsPerPage]
  );

  // 3. Debounce
  const debouncedSearch = useCallback(
    debounce((value: string, deptId: string | null, ecodimeClassId: string | null, isChild: boolean) => {
      setCurrentPage(0);
      loadData(value, 0, deptId, ecodimeClassId, isChild);
    }, 500),
    [loadData]
  );

  const onSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);
    debouncedSearch(value, deptIdFromUrl, ecodimeClassIdFromUrl, isChildFromUrl);
  };

  // 4. Gestion des filtres
  const handleDeptChange = (selected: any) => {
    const newDeptId = selected ? selected.value : null;
    const params = new URLSearchParams(location.search);
    if (newDeptId) params.set("deptId", newDeptId);
    else params.delete("deptId");
    history.push({ search: params.toString() });
    setCurrentPage(0);
  };

  const handleEcodimeChange = (selected: any) => {
    const newEcodimeId = selected ? selected.value : null;
    const params = new URLSearchParams(location.search);
    if (newEcodimeId) params.set("ecodimeClassId", newEcodimeId);
    else params.delete("ecodimeClassId");
    history.push({ search: params.toString() });
    setCurrentPage(0);
  };

  const handleIsChildChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked;
    const params = new URLSearchParams(location.search);
    if (checked) {
      params.set("isChild", "true");
    } else {
      params.delete("isChild");
    }
    history.push({ search: params.toString() });
    setCurrentPage(0);
  };

  // 5. Effet d'écoute centralisé
  useEffect(() => {
    loadData(searchTerm, currentPage, deptIdFromUrl, ecodimeClassIdFromUrl, isChildFromUrl);
  }, [currentPage, deptIdFromUrl, ecodimeClassIdFromUrl, isChildFromUrl, loadData]);

  // 6. Suppression d'un membre avec contrôle de sécurité défensif
  const handleDelete = async (id: number) => {
    if (!isAdmin) {
      alert("Action non autorisée : seuls les administrateurs peuvent supprimer un membre.");
      return;
    }

    if (window.confirm("Supprimer définitivement ce membre ?")) {
      setLoading(true);
      try {
        await MemberService.delete(id);
        loadData(searchTerm, currentPage, deptIdFromUrl, ecodimeClassIdFromUrl, isChildFromUrl);
      } catch (error: any) {
        if (error?.response?.status === 403) {
          alert("Vous n'avez pas les droits nécessaires pour effectuer cette action.");
        } else {
          alert("Suppression impossible car ce membre est lié à un historique de traitements");
        }
      } finally {
        setLoading(false);
      }
    }
  };

  const totalPages = Math.ceil(totalItems / itemsPerPage);

  const renderGenderIcon = (gender?: Gender | null) => {
    if (gender === "MALE") return <i className="fas fa-mars text-blue-400" title="Masculin"></i>;
    if (gender === "FEMALE") return <i className="fas fa-venus text-pink-400" title="Féminin"></i>;
    return <span className="text-slate-600">—</span>;
  };

const customStyles = {
    control: (base: any, state: any) => ({
      ...base,
      backgroundColor: "#1e293b", // bg-slate-800
      borderColor: state.isFocused ? "#D4AF37" : "#334155", // border-slate-700 / Focus Or
      color: "#ffffff",
      padding: "0 2px",
      minHeight: "38px",
      height: "38px",
      borderRadius: "0.5rem",
      boxShadow: "none",
      fontSize: "0.875rem", // text-sm
      fontFamily: "ui-sans-serif, system-ui, sans-serif",
      "&:hover": { borderColor: state.isFocused ? "#D4AF37" : "#475569" },
    }),
    
    // Placeholder (couleur identique au champ de recherche classique)
    placeholder: (base: any) => ({
      ...base,
      color: "#94a3b8", // text-slate-400
      fontSize: "0.875rem",
    }),

    // Texte saisi lors de la frappe dans le Select
    input: (base: any) => ({
      ...base,
      color: "#ffffff", // Blanc pur lors de la saisie
    }),

    // Valeur sélectionnée affichée
    singleValue: (base: any) => ({
      ...base,
      color: "#ffffff", // Blanc pur
      fontWeight: "500",
    }),

    menu: (base: any) => ({
      ...base,
      backgroundColor: "#1e293b",
      border: "1px solid #334155",
      borderRadius: "0.5rem",
      zIndex: 9999,
      overflow: "hidden",
    }),

    menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),

    option: (base: any, state: any) => ({
      ...base,
      backgroundColor: state.isSelected
        ? "#D4AF37"
        : state.isFocused
        ? "#334155"
        : "transparent",
      color: state.isSelected ? "#0f172a" : "#ffffff",
      fontSize: "0.875rem",
      cursor: "pointer",
      "&:active": {
        backgroundColor: "#D4AF37",
        color: "#0f172a",
      },
    }),
  };

  return (
    <div className="flex flex-col w-full min-h-screen mt-3 pt-24 justify-start px-4">
      {/* BARRE DE FILTRES MULTICRITÈRES */}
      <div className="flex flex-col gap-4 mb-8">
        {/* LIGNE 1 : Recherche + Sélection Département/Écodime + Bouton d'ajout */}
        <div className="flex flex-col lg:flex-row items-center gap-4">
          <div className="relative flex-1 group w-full">
            <i className="fas fa-search absolute left-4 top-1/2 -translate-y-1/2 text-slate-500 text-sm"></i>
            <input
              type="text"
              value={searchTerm}
              onChange={onSearchChange}
              placeholder="Rechercher par nom, prénom, email..."
              className="w-full bg-slate-800 border border-slate-700 text-white text-sm rounded-lg pl-11 pr-4 py-2 font-sans h-[38px] outline-none focus:border-blue-500 transition-colors"
            />
          </div>

          <div className="w-full md:flex-1 lg:w-64">
            <Select
              options={deptOptions}
              styles={customStyles}
              placeholder="Tous les départements"
              isClearable
              value={deptOptions.find((o) => o.value === deptIdFromUrl) || null}
              onChange={handleDeptChange}
              menuPortalTarget={document.body}
            />
          </div>

          <div className="w-full md:flex-1 lg:w-64">
            <Select
              options={ecodimeOptions}
              styles={customStyles}
              placeholder="Toutes les classes Écodime"
              isClearable
              value={ecodimeOptions.find((o) => o.value === ecodimeClassIdFromUrl) || null}
              onChange={handleEcodimeChange}
              menuPortalTarget={document.body}
            />
          </div>

          <Link
            to="/admin/members/register"
            className="bg-[#D4AF37] hover:bg-[#b8952e] text-slate-900 font-bold px-8 h-[38px] flex items-center justify-center rounded-lg whitespace-nowrap transition-colors w-full lg:w-auto text-center text-sm"
          >
            Nouveau Membre
          </Link>
        </div>

        {/* LIGNE 2 : Case à cocher "Afficher uniquement les enfants" */}
        <div className="flex items-center justify-start pl-1">
          <label className="inline-flex items-center cursor-pointer select-none group py-1">
            <input
              type="checkbox"
              checked={isChildFromUrl}
              onChange={handleIsChildChange}
              className="w-4 h-4 text-[#D4AF37] bg-slate-800 border-slate-600 rounded focus:ring-[#D4AF37] focus:ring-2 cursor-pointer accent-[#D4AF37]"
            />
            <span className="ml-2.5 text-xs font-semibold text-slate-300 bg-slate-800 border border-slate-700 px-3 py-1.5 rounded-lg group-hover:border-slate-500 group-hover:text-[#D4AF37] transition-all flex items-center gap-2 shadow-sm">
              <i className="fas fa-child text-sky-400 text-sm"></i> 
              <span>Afficher uniquement les enfants</span>
            </span>
          </label>
        </div>
      </div>

      {/* TABLEAU */}
      <div className="relative flex flex-col min-w-0 break-words w-full mb-10 shadow-2xl rounded-2xl bg-slate-800 border border-slate-700 overflow-hidden">
        <div className="block w-full overflow-x-auto">
          <table className="items-center w-full bg-transparent border-collapse text-left">
            <thead>
              <tr className="bg-[#1E293B] text-slate-300 uppercase text-[10px] font-black tracking-widest border-b border-slate-700">
                <th className="px-6 py-5">Membre</th>
                <th className="px-4 py-5 text-center">Genre</th>
                <th className="px-6 py-5 text-center">Téléphone</th>
                <th className="px-6 py-5">Structure / Département(s)</th>
                <th className="px-6 py-5">Date D'intégration</th>
                <th className="px-6 py-5 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-700/50 text-slate-300">
              {loading ? (
                <tr>
                  <td colSpan={6} className="text-center py-16 text-slate-400">
                    <i className="fas fa-circle-notch animate-spin mr-2"></i> Chargement des données...
                  </td>
                </tr>
              ) : members.length === 0 ? (
                <tr>
                  <td colSpan={6} className="text-center py-16 text-slate-500 italic text-sm">
                    <i className="fas fa-user-slash block text-3xl mb-3 opacity-20"></i> Aucun membre trouvé.
                  </td>
                </tr>
              ) : (
                members.map((member) => (
                  <tr key={member.id} className="hover:bg-slate-700/40 transition-colors group">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="w-10 h-10 rounded-full bg-slate-900 flex items-center justify-center text-[#D4AF37] font-bold mr-4 border border-slate-700 overflow-hidden">
                          {member.photoLink ? (
                            <img
                              src={
                                member.photoLink.startsWith("http")
                                  ? member.photoLink
                                  : `${process.env.REACT_APP_API_URL?.replace(/\/api$/, "")}${member.photoLink}`
                              }
                              alt="Avatar"
                              className="w-full h-full object-cover"
                            />
                          ) : member.lastName ? (
                            member.lastName.charAt(0).toUpperCase()
                          ) : (
                            "M"
                          )}
                        </div>
                        <div className="text-sm font-bold text-white uppercase tracking-tight">
                          {member.lastName} {member.firstName}
                          {member.isChild && (
                            <span className="ml-2 px-1.5 py-0.5 text-[9px] bg-sky-500/20 text-sky-400 border border-sky-500/30 rounded font-normal normal-case">
                              Enfant
                            </span>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-4 text-center text-lg">{renderGenderIcon(member.gender)}</td>
                    <td className="px-6 py-4 text-xs font-medium text-center">
                      <span className="bg-slate-900/50 px-3 py-1.5 rounded-lg border border-slate-700">
                        {member.phoneNumber || "—"}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-xs font-medium">
                      {member.isChild ? (
                        member.ecodimeClassName ? (
                          <span className="bg-amber-500/10 text-amber-400 px-2.5 py-1 rounded-lg border border-amber-500/20 font-bold tracking-wide uppercase text-[9px] inline-block">
                            <i className="fas fa-graduation-cap mr-1"></i> {member.ecodimeClassName}
                          </span>
                        ) : (
                          <span className="text-slate-500 italic text-[11px]">Écodime non assignée</span>
                        )
                      ) : member.departments && member.departments.length > 0 ? (
                        <div className="flex flex-wrap gap-1.5 max-w-xs">
                          {member.departments.map((dept: IDepartmentFlatDTO) => (
                            <span
                              key={dept.id}
                              className="bg-blue-500/10 text-blue-400 border border-blue-500/20 px-2 py-0.5 rounded text-[10px] font-semibold"
                            >
                              {dept.name}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span className="text-slate-500 italic text-[11px]">Aucun département</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-xs text-slate-400">{member.joinDate || "—"}</td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex justify-end gap-5">
                        <Link
                          to={`/admin/members/view/${member.id}`}
                          className="text-slate-400 hover:text-blue-400 transition-colors"
                        >
                          <i className="fas fa-eye text-lg"></i>
                        </Link>
                        <Link
                          to={`/admin/members/edit/${member.id}`}
                          className="text-slate-400 hover:text-[#D4AF37] transition-colors"
                        >
                          <i className="fas fa-edit text-lg"></i>
                        </Link>

                        {/* Rendu conditionnel du bouton de suppression selon les droits ADMIN */}
                        {isAdmin && (
                          <button
                            onClick={() => handleDelete(member.id)}
                            className="text-slate-400 hover:text-red-500 transition-colors"
                            title="Supprimer le membre"
                          >
                            <i className="fas fa-trash-alt text-lg"></i>
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* PAGINATION */}
        <div className="px-6 py-5 bg-[#0F172A]/50 border-t border-slate-700 flex items-center justify-between">
          <p className="text-[11px] text-slate-500 font-bold uppercase tracking-wider">
            Total : <span className="text-[#D4AF37]">{totalItems}</span> {isChildFromUrl ? "enfants" : "membres"}
          </p>
          <div className="flex gap-2">
            <button
              disabled={currentPage === 0 || loading}
              onClick={() => setCurrentPage((p) => p - 1)}
              className="px-5 py-2 text-[10px] font-black uppercase rounded-lg bg-slate-900 text-white border border-slate-700 disabled:opacity-20 hover:bg-slate-700 transition-all shadow-md"
            >
              Précédent
            </button>
            <button
              disabled={currentPage + 1 >= totalPages || loading}
              onClick={() => setCurrentPage((p) => p + 1)}
              className="px-5 py-2 text-[10px] font-black uppercase rounded-lg bg-slate-900 text-white border border-slate-700 disabled:opacity-20 hover:bg-slate-700 transition-all shadow-md"
            >
              Suivant
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}